'use client';
/**
 * LES RÈGLES D'AFFECTATION — l'écran où le RQ les gère elle-même (lot 2).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Les règles d'affectation au planning, je voudrais les voir et les gérer
 *  moi-même. Car j'en ai souvent des nouvelles. » — RQ, 13/09/2026
 *
 * ── Les trois choses que cet écran doit faire, et dans cet ordre ───────────
 *
 * ① LES MONTRER. C'était la demande première, avant même de pouvoir les
 *    écrire : jusqu'ici les règles vivaient dans le code, donc nulle part pour
 *    qui ne le lit pas. Une règle qu'on ne peut pas relire ne se discute pas,
 *    ne s'audite pas, et finit par être appliquée sans que personne sache
 *    qu'elle existe.
 * ② DIRE CE QU'ELLES FONT, en français, composé DEPUIS la règle. Le libellé
 *    saisi est un titre ; la phrase est le contrat. Les confondre laisserait
 *    une règle nommée « après-midi » viser les matins sans que rien n'alerte.
 * ③ MONTRER LEUR PORTÉE RÉELLE. Voir plus bas — c'est le piège du fichier.
 *
 * ⚠️ RIEN NE SE SUPPRIME, tout se DÉSACTIVE. Une règle retirée garde son
 * intérêt : on relit pourquoi elle existait avant de la remettre, et le journal
 * renvoie à une référence qui existe encore.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import {
  I, card, inputStyle, TitrePage, EmptyState, Selecteur,
  Modale as Fenetre, MarqueIA, boutonIA, RefISO, enChargement, Skeleton } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
interface Regle {
  documentId: string; ref: string; libelle: string; actif: boolean; ordre: number;
  verbe: string; portee: { postes?: string[]; plages?: string[]; jours?: number[] };
  sujet: { type?: string; valeur?: string; avec?: string; niveau?: string; jours?: string; debut?: string };
  aumoins: number | null; motif: string; phrase: string;
}
interface Charge {
  regles: Regle[];
  plages: { cle: string; postes: string[] }[];
  postes: { code: string; libelle: string }[];
}

const VERBES = [
  { cle: 'preferer', label: 'Préférer', aide: 'Fait remonter les candidats qui répondent. N’écarte JAMAIS personne : une case ne restera pas vide à cause de cette règle.' },
  { cle: 'exiger', label: 'Exiger', aide: 'Demande au moins N personnes qui répondent, SUR LA CASE. Si c’est impossible, la case est remplie quand même et le manquement est nommé dans l’aperçu.' },
  { cle: 'interdire', label: 'Interdire', aide: '⚠️ Écarte le candidat. C’est le SEUL verbe qui peut laisser un poste vide — à réserver à ce qui ne se négocie pas.' },
];

const SUJETS = [
  { cle: 'sexe', label: 'Sexe', valeurAide: '« F » ou « M »' },
  { cle: 'fonction', label: 'Fonction', valeurAide: 'identifiant de fonction' },
  { cle: 'habilitation', label: 'Habilitation', valeurAide: 'code d’habilitation' },
  { cle: 'anciennete', label: 'Ancienneté', valeurAide: 'nombre de MOIS, au moins' },
  { cle: 'personne', label: 'Une personne nommée', valeurAide: 'son identifiant' },
  { cle: 'continuite', label: 'Tenait déjà ce poste', valeurAide: 'durée maximale, en SEMAINES' },
  { cle: 'dejaTenuRecemment', label: 'A tenu ce poste récemment', valeurAide: 'nombre de JOURS (1 = la veille)' },
  { cle: 'binome', label: 'Deux personnes ensemble', valeurAide: 'la première ; la seconde juste après' },
  /* 30/09/2026 — ordonne les candidats d'un dimanche / férié, le moins chargé d'abord. Verbe : préférer. */
  { cle: 'equilibreDimFer', label: 'Équilibrer les dimanches et fériés', valeurAide: 'fenêtre en SEMAINES passées (12 conseillé)' },
  /* 30/09/2026 — repos entre deux dimanches / fériés. Verbe : préférer. */
  { cle: 'espacerDimFer', label: 'Espacer les dimanches et fériés', valeurAide: 'intervalle en JOURS (14 = pas deux dimanches de suite)' },
  /* 01/10/2026 — deux personnes toujours en plages opposées, bascule à la mi-mois. Verbe : préférer. */
  { cle: 'alternanceMatinSoir', label: 'Alternance matin / après-midi', valeurAide: 'la première personne ; la seconde juste après' },
  /* 01/10/2026 — chaque technicien passe par chaque poste technique au moins un mois par an. Verbe : préférer. */
  /* 01/10/2026 — les jeunes en titulaires sur des postes précis, sous dérogation du RQ. Verbe : préférer. */
  { cle: 'titulaireEnDerogation', label: 'Titulaires sous dérogation', valeurAide: 'identifiants séparés par des virgules ; visez des postes précis' },
  /* 01/10/2026 — techniciens en formation, en doublon, un poste technique différent par mois. Verbe : préférer. */
  { cle: 'formationTournante', label: 'Formation en doublon, tournante', valeurAide: 'identifiants séparés par des virgules' },
  /* 01/10/2026 — deux personnes un dimanche sur deux sur un poste. Verbe : préférer. */
  { cle: 'alternanceDimanche', label: 'Un dimanche sur deux', valeurAide: 'la première personne ; la seconde juste après' },
  /* 01/10/2026 — au moins une personne du groupe, chaque jour, sur l'un des postes visés. Verbe : préférer. */
  /* 03/10/2026 — « ceux qui tiennent les gardes » : les habilités au poste GARDE. Tous les verbes. */
  /* 03/10/2026 — « de garde, pas posté le lendemain » : interdire · GARDE la veille. Tous les verbes. */
  /* 03/10/2026 — garde sur le 1er poste visé (P6/P7), repos le lendemain, puis le poste de 7 h (2e visé). Préférer. */
  { cle: 'roulementGarde', label: 'Roulement de garde', valeurAide: 'code du poste de garde (GARDE) ; visez DEUX postes : celui du garde (P6 ou P7), puis le poste de 7 h' },
  { cle: 'tenuLaVeille', label: 'A tenu un poste la veille', valeurAide: 'code du poste (ex. GARDE) ; sans poste visé = tous les postes' },
  { cle: 'habiliteAuPoste', label: 'Habilités à un poste', valeurAide: 'code du poste (ex. GARDE = ceux qui tiennent les gardes)' },
  { cle: 'presenceSurPostes', label: 'Présence des habilités à un poste', valeurAide: 'code du poste qui définit le groupe (ex. GARDE) ; visez les postes à couvrir' },
  /* 01/10/2026 — au plus N dimanches/fériés par mois et par personne (garde non comptée). Verbe : préférer. */
  { cle: 'plafondDimFerMois', label: 'Plafond de dimanches et fériés par mois', valeurAide: 'nombre maximum par personne (2 conseillé)' },
  { cle: 'rotationTechnique', label: 'Rotation annuelle des techniciens', valeurAide: 'JOURS dans un mois pour qu’un poste compte comme tenu (10 conseillé)' },
];

const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const LIB_PLAGE: Record<string, string> = { matin: 'Matin', apresmidi: 'Après-midi', nuit: 'Nuit' };

const VIDE: Partial<Regle> = {
  ref: '', libelle: '', actif: true, ordre: 100, verbe: 'preferer',
  portee: {}, sujet: { type: 'sexe', valeur: '' }, aumoins: 1, motif: '',
};

export default function ReglesAffectation() {
  const { showToast, session } = useGed();
  const [data, setData] = React.useState<Charge | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [edite, setEdite] = React.useState<Partial<Regle> | null>(null);
  const [envoi, setEnvoi] = React.useState(false);
  // Toute la gestion du planning relève du seul RH (RQ, 03/10/2026).
  const estRH = session.roles.includes('rh');
  /* ── LOT 3 : DIRE SA RÈGLE, LA VOIR TRADUITE ──────────────────────────────
     ⚠️ L'assistance REMPLIT le formulaire, elle n'enregistre RIEN. La RQ lit,
     corrige, puis enregistre — par le même bouton que si elle avait tout tapé.
     Et si le moteur est absent ou trop lent, le formulaire reste entier : cette
     aide raccourcit la saisie, elle ne la conditionne pas. */
  /* ⚠️ `iaTaches` ET PAS SEULEMENT `iaDisponible` : un moteur en service ne
     sert pas forcément CETTE tâche — le catalogue la réserve au rôle RQ, et le
     serveur peut très bien répondre sans la proposer. Se contenter du premier
     drapeau afficherait un bouton qui reviendrait en « tâche inconnue ». */
  const { iaDisponible, iaTaches } = useGed();
  const traductionOfferte = iaDisponible && iaTaches.includes('planning-regle');
  const [phrase, setPhrase] = React.useState('');
  const [traduction, setTraduction] = React.useState(false);
  const [remarqueIA, setRemarqueIA] = React.useState('');
  const [defautsIA, setDefautsIA] = React.useState<string[]>([]);

  const traduire = async () => {
    const p = phrase.trim();
    if (!p || traduction) return;
    setTraduction(true); setRemarqueIA(''); setDefautsIA([]);
    try {
      const r = await fetchEcriture('/api/ged/ged/rh/regles-affectation/proposer', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ phrase: p }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { showToast(d?.error?.message || 'Traduction impossible.', 'erreur'); return; }
      /* La proposition remplit le formulaire — elle ne part nulle part. */
      setEdite({ ...(d.proposition || {}) });
      setRemarqueIA(String(d.remarque || ''));
      /* ⚠️ LES DÉFAUTS SONT MONTRÉS, PAS CORRIGÉS EN SILENCE : corriger sans le
         dire ferait enregistrer autre chose que ce que l'écran affiche. */
      setDefautsIA(Array.isArray(d.defauts) ? d.defauts : []);
    } finally { setTraduction(false); }
  };

  const charger = React.useCallback(() => {
    fetch('/api/ged/ged/rh/regles-affectation', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Lecture refusée'))))
      .then((d) => { setData(d); setErreur(''); })
      /* ⚠️ `null` reste `null` : une liste vide se lirait « aucune règle », ce
         qui est une AFFIRMATION. On dit qu'on n'a pas pu lire. */
      .catch((e) => { setData(null); setErreur(e instanceof Error ? e.message : 'Erreur serveur'); });
  }, []);
  React.useEffect(charger, [charger]);

  const enregistrer = async () => {
    if (!edite) return;
    setEnvoi(true);
    try {
      const r = await fetchEcriture('/api/ged/ged/rh/regles-affectation', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(edite),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { showToast(d?.error?.message || 'Règle refusée.', 'erreur'); return; }
      showToast(`Règle ${d.ref} enregistrée — ${d.phrase}`);
      setEdite(null); charger();
    } finally { setEnvoi(false); }
  };

  const basculer = async (r: Regle) => {
    const res = await fetchEcriture(`/api/ged/ged/rh/regles-affectation/${encodeURIComponent(r.ref)}/etat`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ actif: !r.actif }),
    });
    const d = await res.json().catch(() => null);
    if (!res.ok) { showToast(d?.error?.message || 'Refusé.', 'erreur'); return; }
    showToast(r.actif ? `${r.ref} désactivée.` : `${r.ref} activée.`);
    charger();
  };

  const maj = (k: string, v: unknown) => setEdite((e) => ({ ...(e || {}), [k]: v }));
  const majSujet = (k: string, v: unknown) => setEdite((e) => ({ ...(e || {}), sujet: { ...(e?.sujet || {}), [k]: v } }));
  const majPortee = (k: string, v: unknown) => setEdite((e) => ({ ...(e || {}), portee: { ...(e?.portee || {}), [k]: v } }));

  const sujetCourant = SUJETS.find((s) => s.cle === edite?.sujet?.type);
  const verbeCourant = VERBES.find((v) => v.cle === edite?.verbe);

  return (
    <section>
      <TitrePage eyebrow={<>Plannings &amp; RH</>} titre="Règles d’affectation"
        sousTitre={<>
          Ce que le prévisionnel doit respecter quand il remplit les cases. Les règles sont des
          <strong> données</strong> : vous les écrivez, les activez et les retirez sans passer par
          le développement. Elles <strong>s’ajoutent</strong> aux garde-fous du moteur — habilitation,
          absence validée, repos hebdomadaire — qu’aucune règle ne peut défaire. <RefISO code="6.2" />
        </>}
        actions={estRH ? (
          <Bouton variante="primaire" type="button" onClick={() => setEdite({ ...VIDE })}>
            <I n="plus" s={13} /> Nouvelle règle
          </Bouton>
        ) : undefined} />

      {/* ⚠️ « PAS PU LIRE » SE DIT — un état vide est une affirmation. */}
      {!data && erreur && (
        <div style={{ ...card, padding: 14, marginBottom: 14, background: 'var(--amber-50)', border: '1px solid var(--amber-600)', fontSize: 12.5, lineHeight: 1.55 }}>
          <strong style={{ color: 'var(--amber-600)' }}>Les règles n’ont pas pu être lues</strong> ({erreur}).
          <br />Cet écran ne dit donc <strong>pas</strong> qu’il n’y en a aucune : il dit qu’il ne sait pas.
        </div>
      )}
      {enChargement(!!data || !!erreur, !data && !erreur) && <Skeleton rows={3} />}

      {!!data && !data.regles.length && (
        <EmptyState titre="Aucune règle écrite."
          indice="Le prévisionnel applique ses sept garde-fous, et rien de plus. Créez une règle pour y ajouter vos préférences." />
      )}

      {(data?.regles || []).map((r) => (
        <div key={r.ref} style={{ ...card, padding: 14, marginBottom: 10, opacity: r.actif ? 1 : 0.6 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: 'var(--color-primary-active)' }}>{r.ref}</span>
            <strong style={{ fontSize: 13.5, flex: 1 }}>{r.libelle}</strong>
            <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 9px',
              background: r.actif ? 'var(--green-50)' : 'var(--gray-50)', color: r.actif ? 'var(--green-600)' : 'var(--text-muted)' }}>
              {r.actif ? 'Active' : 'Inactive'}
            </span>
            {estRH && (
              <>
                <Bouton taille="sm" type="button" onClick={() => setEdite({ ...r })}>Modifier</Bouton>
                {/* ⚠️ DUPLIQUER (13/09/2026, RQ : « je voudrais pouvoir les
                    dupliquer »). On repart de la règle SOURCE, mais SANS sa
                    `ref` — enregistrer en pose une nouvelle, ce n'est jamais
                    une réécriture de la source. Le libellé porte « (copie) » :
                    sans lui, deux règles identiques dans la liste se
                    distingueraient seulement par leur référence technique,
                    que personne ne lit. */}
                <Bouton taille="sm" type="button" onClick={() => setEdite({ ...r, ref: '', libelle: `${r.libelle} (copie)` })}>
                  Dupliquer
                </Bouton>
                {/* ⚠️ DÉSACTIVER, PAS SUPPRIMER : on relit pourquoi une règle
                    existait avant de la remettre, et le journal renvoie à une
                    référence qui existe encore. Le libellé du bouton reste
                    court ; c'est l'infobulle qui porte l'explication, pour
                    qui demande « supprimer » et ne trouve pas la corbeille. */}
                <Bouton taille="sm" type="button" onClick={() => void basculer(r)} title={r.actif
                    ? 'Désactivée, elle n’agira plus lors des générations — l’historique reste pour la traçabilité.'
                    : 'La règle recommence à agir lors des prochaines générations.'}>
                  {r.actif ? 'Désactiver' : 'Activer'}
                </Bouton>
              </>
            )}
          </div>
          {/* ⚠️ LA PHRASE EST LE CONTRAT, le libellé n'est qu'un titre. Elle est
              composée par le moteur DEPUIS la règle : une règle nommée
              « après-midi » qui viserait les matins se verrait ici. */}
          <p style={{ margin: '6px 0 0', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>{r.phrase}</p>
          {r.motif
            ? <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>Motif : {r.motif}</p>
            : (
              /* Pas une faute, un signalement : une règle d'affectation qui
                 porte son motif se défend seule devant un audit. */
              <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--amber-600)', lineHeight: 1.5 }}>
                Aucun motif écrit — c’est le premier point qu’un contrôle relèvera.
              </p>
            )}
        </div>
      ))}

      {/* ── ⚠️ CE QUE CHAQUE PLAGE ATTRAPE RÉELLEMENT ───────────────────────
          Le piège de cet écran. « Après-midi » n'est écrit nulle part dans les
          postes : la plage est DÉDUITE de l'heure de début. P4 Hémato
          (09:00→17:00) tombe donc en « matin » alors qu'il couvre l'après-midi.
          Sans cette liste, on poserait une règle en croyant viser six postes et
          on en viserait quatre — sans que rien ne le dise jamais. */}
      {!!data?.plages?.length && (
        <div style={{ ...card, padding: 14, marginTop: 16 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700 }}>Ce que chaque plage vise</h3>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            La plage se déduit de l’<strong>heure de début</strong> du poste — elle n’est saisie nulle part.
            Vérifiez la liste : si un poste n’y est pas où vous l’attendez, visez-le par son code.
          </p>
          {data.plages.map((p) => (
            <div key={p.cle} style={{ fontSize: 12.5, lineHeight: 1.6 }}>
              <strong>{LIB_PLAGE[p.cle] || p.cle}</strong> — {p.postes.length ? p.postes.join(', ') : <em>aucun poste</em>}
            </div>
          ))}
        </div>
      )}

      {/* ── DIRE SA RÈGLE EN FRANÇAIS (lot 3, 13/09/2026) ───────────────────
          ⚠️ Affiché SEULEMENT si un moteur est configuré. Un champ qui promet
          une traduction et rend une erreur au bout de quatre minutes vaut moins
          que pas de champ du tout — et le formulaire manuel, lui, marche
          toujours. */}
      {estRH && traductionOfferte && (
        <div style={{ ...card, padding: 14, marginTop: 16 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700 }}>Dire la règle en une phrase</h3>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            L’assistant la traduit dans les briques ci-dessus et <strong>remplit le formulaire</strong>.
            Il n’enregistre rien : vous relisez, vous corrigez, et c’est vous qui validez.
            {' '}<strong>Comptez plusieurs minutes</strong> — le moteur tourne sur le serveur du laboratoire.
          </p>
          <textarea value={phrase} onChange={(e) => setPhrase(e.target.value)} rows={2}
            placeholder="Une personne qui vient d’être habilitée ne doit jamais être seule sur une paillasse."
            style={{ ...inputStyle, height: 'auto', padding: '8px 10px' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Bouton taille="sm" type="button" style={boutonIA} disabled={traduction || !phrase.trim()} onClick={() => void traduire()}>
              <MarqueIA s={13} /> {traduction ? 'Traduction en cours…' : 'Traduire en règle'}
            </Bouton>
          </div>
        </div>
      )}

      {edite && (
        <Fenetre titre={edite.ref ? `Règle ${edite.ref}` : 'Nouvelle règle'} largeur={680}
          onClose={() => setEdite(null)}
          pied={<>
            <Bouton taille="sm" type="button" onClick={() => setEdite(null)}>Annuler</Bouton>
            <Bouton variante="primaire" type="button" disabled={envoi} onClick={() => void enregistrer()}>
              {envoi ? 'Enregistrement…' : 'Enregistrer la règle'}
            </Bouton>
          </>}>
          <div style={{ display: 'grid', gap: 12 }}>
            {/* ⚠️ CE QUE L'ASSISTANT A DÛ CHOISIR, dit avant tout le reste. Le
                taire ferait passer une interprétation pour une lecture. */}
            {!!remarqueIA && (
              <div style={{ fontSize: 12.5, lineHeight: 1.55, padding: '10px 12px', borderRadius: 'var(--radius-md)',
                background: 'var(--cyan-50)', border: '1px solid var(--cyan-200)' }}>
                <strong style={{ color: 'var(--cyan-700)' }}>Ce que l’assistant signale</strong>
                <br />{remarqueIA}
              </div>
            )}
            {/* ⚠️ LES DÉFAUTS SONT MONTRÉS, PAS CORRIGÉS EN SILENCE. */}
            {!!defautsIA.length && (
              <div style={{ fontSize: 12.5, lineHeight: 1.55, padding: '10px 12px', borderRadius: 'var(--radius-md)',
                background: 'var(--amber-50)', border: '1px solid var(--amber-600)' }}>
                <strong style={{ color: 'var(--amber-600)' }}>La proposition est incomplète — corrigez avant d’enregistrer</strong>
                <ul style={{ margin: '5px 0 0', paddingInlineStart: 18 }}>
                  {defautsIA.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            )}
            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Libellé
              <input value={edite.libelle || ''} onChange={(e) => maj('libelle', e.target.value)}
                placeholder="Au moins un homme l’après-midi" style={inputStyle} />
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                Un titre, pour la retrouver. Ce que la règle FAIT est composé plus bas, depuis les briques.
              </span>
            </label>

            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Verbe
              <Selecteur value={edite.verbe || ''} onChange={(v) => maj('verbe', v)} style={inputStyle}>
                {VERBES.map((v) => <option key={v.cle} value={v.cle}>{v.label}</option>)}
              </Selecteur>
              {/* Ce que le verbe fait, dit AVANT de choisir : c'est la seule
                  décision de cet écran qui puisse laisser un poste vide. */}
              {!!verbeCourant && (
                <span style={{ fontSize: 11.5, lineHeight: 1.5, color: verbeCourant.cle === 'interdire' ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                  {verbeCourant.aide}
                </span>
              )}
            </label>

            {edite.verbe === 'exiger' && (
              <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Au moins
                <input type="number" min={1} value={edite.aumoins ?? 1}
                  onChange={(e) => maj('aumoins', Number(e.target.value))} style={inputStyle} />
              </label>
            )}

            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Sujet — sur qui la règle porte
              <Selecteur value={edite.sujet?.type || ''} onChange={(v) => majSujet('type', v)} style={inputStyle}>
                {SUJETS.map((s) => <option key={s.cle} value={s.cle}>{s.label}</option>)}
              </Selecteur>
            </label>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Valeur
              <input value={edite.sujet?.valeur || ''} onChange={(e) => majSujet('valeur', e.target.value)}
                placeholder={sujetCourant?.valeurAide} style={inputStyle} />
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{sujetCourant?.valeurAide}</span>
            </label>
            {edite.sujet?.type === 'formationTournante' && (
              <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Durée de la formation <span style={{ color: 'var(--text-muted)' }}>(jours ouvrés, facultatif)</span>
                <input value={edite.sujet?.jours || ''} onChange={(e) => majSujet('jours', e.target.value)} style={inputStyle} placeholder="6 = une semaine" />
                <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  Passé ce délai, la personne tient le poste en titulaire, sous dérogation jusqu’à ce que l’habilitation soit actée. Vide = formation tout le mois.
                </span>
              </label>
            )}
            {edite.sujet?.type === 'formationTournante' && (
              <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Début de la formation <span style={{ color: 'var(--text-muted)' }}>(facultatif)</span>
                <input type="date" value={edite.sujet?.debut || ''} onChange={(e) => majSujet('debut', e.target.value)} style={inputStyle} />
                <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Avant cette date, la personne reste sur les postes ordinaires.</span>
              </label>
            )}
            {(edite.sujet?.type === 'binome' || edite.sujet?.type === 'alternanceMatinSoir' || edite.sujet?.type === 'alternanceDimanche') && (
              <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Seconde personne
                <input value={edite.sujet?.avec || ''} onChange={(e) => majSujet('avec', e.target.value)} style={inputStyle} />
                <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  La règle est symétrique : inutile de l’écrire dans les deux sens.
                </span>
              </label>
            )}
            {edite.sujet?.type === 'habilitation' && (
              <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Niveau exigé (facultatif)
                <input value={edite.sujet?.niveau || ''} onChange={(e) => majSujet('niveau', e.target.value)} style={inputStyle} />
              </label>
            )}

            <div style={{ fontSize: 12.5, display: 'grid', gap: 6 }}>Portée — où la règle s’applique
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Tout laisser vide = <strong>partout, tous les jours</strong>.
              </span>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {['matin', 'apresmidi', 'nuit'].map((p) => (
                  <label key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5 }}>
                    <input type="checkbox" checked={(edite.portee?.plages || []).includes(p)}
                      onChange={(e) => majPortee('plages', e.target.checked
                        ? [...(edite.portee?.plages || []), p]
                        : (edite.portee?.plages || []).filter((x) => x !== p))} />
                    {LIB_PLAGE[p]}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {JOURS.map((j, i) => (
                  <label key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5 }}>
                    <input type="checkbox" checked={(edite.portee?.jours || []).includes(i + 1)}
                      onChange={(e) => majPortee('jours', e.target.checked
                        ? [...(edite.portee?.jours || []), i + 1]
                        : (edite.portee?.jours || []).filter((x) => x !== i + 1))} />
                    {j}
                  </label>
                ))}
              </div>
              <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Postes visés (codes séparés par des virgules)
                <input value={(edite.portee?.postes || []).join(', ')}
                  onChange={(e) => majPortee('postes', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))}
                  placeholder="ACCUEILPM, PLVPM" style={inputStyle} />
              </label>
            </div>

            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Motif
              <textarea value={edite.motif || ''} onChange={(e) => maj('motif', e.target.value)} rows={2}
                placeholder="Pourquoi cette règle existe." style={{ ...inputStyle, height: 'auto', padding: '8px 10px' }} />
              {/* ⚠️ Facultatif, et signalé quand il manque : une règle
                  d'affectation qui porte son motif se défend seule ; une règle
                  muette ne se défend pas. */}
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Facultatif, mais c’est ce qui rend la règle défendable devant un audit — surtout
                lorsqu’elle porte sur une caractéristique des personnes.
              </span>
            </label>
          </div>
        </Fenetre>
      )}
    </section>
  );
}
